import { Plus, Trash, AlertCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { CATEGORIES } from "../utils/Categories";

/**
 * Taxes panel:
 * - No local Save buttons (everything saved via global Save All).
 * - No "No global tax" dropdown. "Apply to all products" lives in each config.
 * - If any config has apply_to_all, hide assignments and disable adding more configs.
 * - Live validation for: missing name, rate >= 0 (0 allowed), exclusivity rules.
 * - Assignments UI: pick a tax on the left; on the right, products grouped by category
 *   with tri-state "select all in category".
 * - Shows a live count of assigned products next to each tax config.
 */
export default function TaxesPanel({ products = [], taxes }) {
  const {
    draftConfigs, setDraftConfigs,
    addDraftConfig, updateDraftConfig, deleteDraftConfig,
    assignments, assignProduct, setForCategory, setAllForTax,
    validation, configsApplyAllActive,
    busy,
  } = taxes;

  const [activeTaxId, setActiveTaxId] = useState(() => draftConfigs?.[0]?.__cidOrId || null);

  // keep activeTaxId valid when list changes
  useMemo(() => {
    const list = draftConfigs || [];
    if (!list.length) {
      setActiveTaxId(null);
      return;
    }
    const exists = list.some(c => c.__cidOrId === activeTaxId);
    if (!exists) setActiveTaxId(list[0].__cidOrId);
  }, [draftConfigs, activeTaxId]);

  // build flat products with main root category for grouping
  const flat = useMemo(() => {
    const norm = (s) => (s||"").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g,"").toLowerCase();
    return (products || []).map(p => ({
      ...p,
      _mainCategory: getMainCategoryName(p?.category || []),
      _normName: norm(p?.name || ""),
    }));
  }, [products]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of flat) {
      const key = p._mainCategory || "Uncategorized";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries()); // [ [category, products[]], ... ]
  }, [flat]);

  const selectedForActive = new Set(assignments[activeTaxId] || []);
  const allIds = flat.map(p => String(p.id));
  const allSelected = activeTaxId && selectedForActive.size === allIds.length && allIds.length > 0;
  const someSelected = activeTaxId && selectedForActive.size > 0 && selectedForActive.size < allIds.length;

  const toggleAll = (on) => {
    if (!activeTaxId) return;
    setAllForTax(activeTaxId, on);
  };

  // master validation banner (live)
  const showValidation = !validation.ok;
  const messages = validation.messages || [];

  return (
    <div className="grid md:grid-cols-[360px,1fr] gap-4">
      {/* LEFT: Tax configs CRUD */}
      <aside className="rounded-xl border bg-white overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="text-sm font-medium">Tax configurations</div>
          <button
            className="btn-secondary rounded-xl flex items-center gap-1 disabled:opacity-60"
            onClick={addDraftConfig}
            disabled={busy || configsApplyAllActive} // cannot add if a config applies to all
            title={configsApplyAllActive ? "A config applies to all products—add more is disabled" : undefined}
          >
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>

        {/* Live validation errors */}
        {showValidation && (
          <div className="m-3 p-2 rounded-md bg-amber-50 text-amber-800 text-xs">
            {messages.map((m, i) => (
              <div key={i} className="flex items-start gap-1">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>{m}</span>
              </div>
            ))}
          </div>
        )}

        <ul className="max-h-[60vh] overflow-y-auto divide-y">
          {draftConfigs.map((c) => {
            const id = c.__cidOrId; // server id or client temp id
            const active = id === activeTaxId;
            const assignedCount = (assignments[id] || []).length;

            return (
              <li key={id} className={`p-3 ${active ? "bg-indigo-50" : ""}`}>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-left flex-1"
                    onClick={() => setActiveTaxId(id)}
                    title="Select for assignments"
                  >
                    <input
                      value={c.name}
                      onChange={(e) => updateDraftConfig(id, { name: e.target.value })}
                      placeholder="Name"
                      className="input text-sm w-full"
                    />
                  </button>

                  <input
                    type="number" min="0" step="0.001"
                    value={c.rate_percent}
                    onChange={(e) => updateDraftConfig(id, { rate_percent: e.target.value })}
                    className="input text-sm w-28"
                    placeholder="% rate"
                    aria-label="Rate percent"
                  />

                  {/* live count of assigned products */}
                  <span
                    className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700"
                    title="Assigned products"
                  >
                    {assignedCount}
                  </span>
                </div>

                <div className="mt-2 flex items-center justify-between">
                  <label className="text-xs flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!c.applies_to_all}
                      onChange={(e) => {
                        const on = e.target.checked;
                        // if toggled on, turn off for all others
                        if (on) {
                          setDraftConfigs(list => list.map(x => ({ ...x, applies_to_all: x.__cidOrId === id })));
                        } else {
                          updateDraftConfig(id, { applies_to_all: false });
                        }
                      }}
                    />
                    Apply to all products
                  </label>

                  <button
                    className="text-red-600 text-xs flex items-center gap-1 disabled:opacity-60"
                    onClick={() => deleteDraftConfig(id)}
                    disabled={!!c.applies_to_all} // don't allow deleting the active "all products" row directly
                    title={c.applies_to_all ? "Disable 'apply to all' first" : "Delete"}
                  >
                    <Trash className="h-4 w-4" /> Delete
                  </button>
                </div>
              </li>
            );
          })}

          {!draftConfigs.length && (
            <li className="p-3 text-sm text-gray-600">No tax configurations yet.</li>
          )}
        </ul>

        <div className="p-3 border-t text-[11px] text-gray-500">
          Changes are saved with <strong>Save All</strong>.
        </div>
      </aside>

      {/* RIGHT: Assignments (hidden when any config applies to all) */}
      <section className="rounded-xl border bg-white overflow-hidden">
        {configsApplyAllActive ? (
          <div className="p-6 text-sm text-gray-700">
            A configuration is set to <strong>apply to all products</strong>. Per-product assignments are disabled.
          </div>
        ) : !activeTaxId ? (
          <div className="p-6 text-sm text-gray-600">Add a tax configuration to start assigning products.</div>
        ) : (
          <>
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b bg-white p-3">
              <div className="flex items-center gap-2 text-sm">
                <TriStateCheckbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onChange={toggleAll}
                />
                <span className="font-medium">Apply selected tax to all products</span>
              </div>
            </div>

            <div className="divide-y">
              {grouped.map(([cat, items]) => {
                const ids = items.map(i => String(i.id));
                const selectedInCat = ids.filter(id => (assignments[activeTaxId] || []).includes(id)).length;
                const catAll = selectedInCat === ids.length && ids.length > 0;
                const catSome = selectedInCat > 0 && selectedInCat < ids.length;

                return (
                  <div key={cat}>
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                      <div className="flex items-center gap-2 text-sm">
                        <TriStateCheckbox
                          checked={catAll ? true : catSome ? "indeterminate" : false}
                          onChange={(on) => setForCategory(activeTaxId, ids, on)}
                        />
                        <span className="font-medium">{cat}</span>
                        <span className="text-xs text-gray-500">({items.length})</span>
                      </div>
                      <div className="text-xs text-gray-500">Select all in category</div>
                    </div>

                    <ul>
                      {items.map((p) => {
                        const pid = String(p.id);
                        const assigned = (assignments[activeTaxId] || []).includes(pid);

                        return (
                          <li key={pid} className="border-t">
                            <div className="flex items-center gap-3 px-3 py-2">
                              <label className="flex items-center gap-2 min-w-[240px]">
                                <input
                                  type="checkbox"
                                  checked={assigned}
                                  onChange={(e) => assignProduct(activeTaxId, pid, e.target.checked)}
                                  aria-label={`Assign active tax to ${p.name}`}
                                />
                                <span className="truncate">{p.name}</span>
                              </label>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/* ---------- tiny bits ---------- */
function getMainCategoryName(pathIds = []) {
  if (!Array.isArray(pathIds) || !pathIds.length) return null;
  const topId = String(pathIds[0]);
  const root = (CATEGORIES || []).find((n) => String(n.id) === topId);
  return root?.name || null;
}

function TriStateCheckbox({ checked, onChange }) {
  // checked: true | false | "indeterminate"
  return (
    <input
      type="checkbox"
      ref={(el) => {
        if (el) el.indeterminate = checked === "indeterminate";
      }}
      checked={checked === true}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}
